/**
 * Zoom Animator
 *
 * Provides smooth, interpolated zoom for mouse wheel events.
 * Uses exponential zoom formula and velocity-adaptive lerp for natural feel.
 *
 * Trackpad zoom bypasses this (OS already provides smooth gestures).
 */

// ============================================================================
// TYPES
// ============================================================================

export interface ZoomAnimatorConfig {
	/** Base lerp factor for slow scrolls (default: 0.12) */
	baseLerpFactor: number;
	/** Max lerp factor for fast scrolls (default: 0.3) */
	maxLerpFactor: number;
	/** Velocity decay per frame (default: 0.85) */
	velocityDecay: number;
	/** Events/sec threshold to increase lerp (default: 3) */
	velocityThreshold: number;
	/** Time window for velocity calculation in ms (default: 200) */
	velocityWindowMs: number;
	/** Exponential zoom base - zoom multiplier per unit delta (default: 1.002) */
	zoomBase: number;
	/** Minimum zoom difference to continue animating (default: 0.0005) */
	restThreshold: number;
}

export interface ZoomAnimatorState {
	/** Target zoom level we're animating toward */
	targetZoom: number;
	/** Current interpolated zoom level */
	currentZoom: number;
	/** Screen position to zoom toward (cursor position) */
	lockPoint: { x: number; y: number } | null;
	/** Current scroll velocity (events per second) */
	velocity: number;
	/** Whether animation loop is running */
	isAnimating: boolean;
}

export interface ZoomAnimatorDependencies {
	/** Get current camera state */
	getCamera(): { x: number; y: number; zoom: number };
	/** Get viewport dimensions */
	getViewport(): { width: number; height: number };
	/** Callback when zoom should be applied - receives factor and origin */
	onZoom(factor: number, origin: { x: number; y: number }): void;
	/** Optional zoom bounds */
	getZoomBounds?(): { min: number; max: number };
}

export interface ZoomAnimator {
	/** Add a wheel delta to the animation queue */
	enqueue(deltaY: number, origin: { x: number; y: number }): void;
	/** Stop current animation */
	stop(): void;
	/** Get current animator state (for debugging) */
	getState(): ZoomAnimatorState;
	/** Update configuration */
	configure(config: Partial<ZoomAnimatorConfig>): void;
	/** Clean up (cancel RAF) */
	destroy(): void;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_CONFIG: ZoomAnimatorConfig = {
	baseLerpFactor: 0.12,
	maxLerpFactor: 0.3,
	velocityDecay: 0.85,
	velocityThreshold: 4,
	velocityWindowMs: 200,
	zoomBase: 1.001,
	restThreshold: 0.0005,
};

// ============================================================================
// IMPLEMENTATION
// ============================================================================

/**
 * Linear interpolation
 */
function lerp(a: number, b: number, t: number): number {
	return a + (b - a) * t;
}

/**
 * Create a zoom animator instance
 */
export function createZoomAnimator(
	deps: ZoomAnimatorDependencies,
	initialConfig?: Partial<ZoomAnimatorConfig>
): ZoomAnimator {
	const config: ZoomAnimatorConfig = {
		...DEFAULT_CONFIG,
		...initialConfig,
	};

	// Animation state
	let state: ZoomAnimatorState = {
		targetZoom: 1,
		currentZoom: 1,
		lockPoint: null,
		velocity: 0,
		isAnimating: false,
	};

	// Internal tracking
	let deltaHistory: Array<{ delta: number; timestamp: number }> = [];
	let accumulatedDelta = 0;
	let lastDirection: 'in' | 'out' | null = null;
	let rafId: number | null = null;
	let previousZoom = 1;
	let startingZoom = 1; // Zoom level at start of current gesture (for target calculation)

	/**
	 * Update velocity based on recent delta events
	 */
	function updateVelocity(delta: number, now: number): void {
		deltaHistory.push({ delta, timestamp: now });

		// Prune old entries
		const cutoff = now - config.velocityWindowMs;
		deltaHistory = deltaHistory.filter((e) => e.timestamp >= cutoff);

		// Calculate events per second
		if (deltaHistory.length > 1) {
			const timeSpan = now - deltaHistory[0].timestamp;
			if (timeSpan > 0) {
				state.velocity = (deltaHistory.length / timeSpan) * 1000;
			}
		} else {
			state.velocity = 0;
		}
	}

	/**
	 * Calculate adaptive lerp factor based on scroll velocity
	 */
	function calculateLerpFactor(): number {
		const v = state.velocity;
		if (v <= config.velocityThreshold) {
			return config.baseLerpFactor;
		}
		// Smooth interpolation from base to max as velocity increases
		const normalizedV = Math.min(1, (v - config.velocityThreshold) / 15);
		return config.baseLerpFactor + normalizedV * (config.maxLerpFactor - config.baseLerpFactor);
	}

	/**
	 * Animation frame callback
	 */
	function animate(): void {
		if (!state.isAnimating) return;

		// Calculate adaptive lerp factor
		const lerpFactor = calculateLerpFactor();

		// Lerp toward target
		state.currentZoom = lerp(state.currentZoom, state.targetZoom, lerpFactor);

		// Decay velocity
		state.velocity *= config.velocityDecay;

		// Check if we've reached rest
		const delta = Math.abs(state.targetZoom - state.currentZoom);
		const relativeThreshold = config.restThreshold * state.currentZoom;

		if (delta < relativeThreshold) {
			// Snap to target and stop
			state.currentZoom = state.targetZoom;

			// Emit final zoom
			if (state.lockPoint && previousZoom !== state.currentZoom) {
				const factor = state.currentZoom / previousZoom;
				deps.onZoom(factor, state.lockPoint);
			}

			stopInternal();
			return;
		}

		// Calculate zoom factor for this frame
		const factor = state.currentZoom / previousZoom;
		previousZoom = state.currentZoom;

		// Dispatch zoom with the lock point
		if (state.lockPoint && factor !== 1) {
			deps.onZoom(factor, state.lockPoint);
		}

		rafId = requestAnimationFrame(animate);
	}

	/**
	 * Internal stop without full reset (preserves zoom level)
	 */
	function stopInternal(): void {
		if (rafId !== null) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		state.isAnimating = false;
		state.lockPoint = null;
		accumulatedDelta = 0;
		lastDirection = null;
		deltaHistory = [];
		startingZoom = state.currentZoom; // Preserve current zoom as starting point for next gesture
	}

	return {
		enqueue(deltaY: number, origin: { x: number; y: number }): void {
			const camera = deps.getCamera();
			const now = typeof performance !== 'undefined' ? performance.now() : Date.now();

			// Detect direction change
			const direction = deltaY < 0 ? 'in' : 'out';
			if (lastDirection !== null && direction !== lastDirection) {
				// Direction changed - reset accumulated delta and starting zoom
				// This lets us smoothly reverse direction from wherever we are
				accumulatedDelta = 0;
				startingZoom = state.currentZoom;
			}
			lastDirection = direction;

			// Accumulate delta (negate for natural zoom direction: scroll up = zoom in)
			accumulatedDelta += -deltaY;

			// Update velocity tracking
			updateVelocity(deltaY, now);

			// Capture lock point on first delta of a gesture
			if (!state.lockPoint) {
				state.lockPoint = { ...origin };
			}

			// Sync current zoom if starting fresh
			if (!state.isAnimating) {
				state.currentZoom = camera.zoom;
				previousZoom = camera.zoom;
				startingZoom = camera.zoom;
			}

			// Calculate target using exponential formula
			// Each unit of delta multiplies zoom by zoomBase
			// Use startingZoom (not currentZoom) to avoid double-counting during animation
			state.targetZoom = startingZoom * Math.pow(config.zoomBase, accumulatedDelta);

			// Clamp to bounds if available
			const bounds = deps.getZoomBounds?.();
			if (bounds) {
				state.targetZoom = Math.max(bounds.min, Math.min(bounds.max, state.targetZoom));
			}

			// Start animation if not running
			if (!state.isAnimating) {
				state.isAnimating = true;
				rafId = requestAnimationFrame(animate);
			}
		},

		stop(): void {
			stopInternal();
		},

		getState(): ZoomAnimatorState {
			return { ...state };
		},

		configure(newConfig: Partial<ZoomAnimatorConfig>): void {
			Object.assign(config, newConfig);
		},

		destroy(): void {
			stopInternal();
		},
	};
}
